import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Shield, ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fetchBeneficials } from "@/lib/api";

const ROLE_COLOR = {
    predator:              { bg: "bg-[var(--vm-sage)]/15",        text: "text-[var(--vm-sage)]",        border: "border-[var(--vm-sage)]/40" },
    pollinator:            { bg: "bg-[var(--vm-secondary)]/20",   text: "text-[var(--vm-secondary)]",   border: "border-[var(--vm-secondary)]/40" },
    parasitoid:            { bg: "bg-[var(--vm-terracotta)]/15",  text: "text-[var(--vm-terracotta)]",  border: "border-[var(--vm-terracotta)]/40" },
    "soil engineer":       { bg: "bg-amber-900/10",               text: "text-amber-800",                border: "border-amber-800/40" },
    "biological control":  { bg: "bg-[var(--vm-accent)]/15",      text: "text-[var(--vm-accent)]",      border: "border-[var(--vm-accent)]/40" },
    "predator + pollinator": { bg: "bg-[var(--vm-sage)]/15",      text: "text-[var(--vm-sage)]",        border: "border-[var(--vm-sage)]/40" },
};

export default function Beneficials() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [role, setRole] = useState("all");

    useEffect(() => {
        fetchBeneficials()
            .then((d) => setList(d.beneficials || []))
            .catch(() => setList([]))
            .finally(() => setLoading(false));
    }, []);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return list.filter((b) => {
            if (role !== "all" && !(b.role || "").includes(role)) return false;
            if (!needle) return true;
            const hay = `${b.name} ${b.role} ${(b.preys_on || []).join(" ")}`.toLowerCase();
            return hay.includes(needle);
        });
    }, [list, q, role]);

    const roles = ["all", "predator", "pollinator", "parasitoid", "biological control"];

    return (
        <div className="space-y-10" data-testid="beneficials-page">
            <PageHeader
                overline="Reference library"
                title="Beneficial insects"
                description="The creatures that keep your garden in balance — 20 predators, pollinators and soil engineers to encourage before you reach for a spray."
            />

            <section className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[16rem]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vm-text-muted)]" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, role or prey…"
                        data-testid="beneficial-search-input"
                        className="pl-9 bg-[var(--vm-card)] border-[var(--vm-border)]"
                    />
                </div>
                <div className="flex flex-wrap gap-1" data-testid="beneficial-role-filter">
                    {roles.map((r) => (
                        <Button
                            key={r}
                            type="button"
                            size="sm"
                            variant={role === r ? "default" : "outline"}
                            onClick={() => setRole(r)}
                            data-testid={`beneficial-role-${r.replace(/\s+/g, "-")}`}
                            className={`rounded-full capitalize ${role === r ? "bg-[var(--vm-sage)] hover:opacity-90 text-[var(--vm-bg)]" : "border-[var(--vm-border)]"}`}
                        >
                            {r}
                        </Button>
                    ))}
                </div>
            </section>

            {loading ? (
                <p className="text-[var(--vm-text-muted)]">Loading…</p>
            ) : filtered.length === 0 ? (
                <div className="journal-card p-8 text-center" data-testid="beneficials-empty">
                    <Shield className="w-8 h-8 text-[var(--vm-text-muted)] mx-auto mb-2" />
                    <p className="text-sm text-[var(--vm-text-muted)]">No matches — try another filter.</p>
                </div>
            ) : (
                <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="beneficials-grid">
                    {filtered.map((b) => {
                        const style = ROLE_COLOR[b.role] || ROLE_COLOR.predator;
                        return (
                            <Link
                                key={b.slug}
                                to={`/beneficials/${b.slug}`}
                                data-testid={`beneficial-card-${b.slug}`}
                                className="journal-card overflow-hidden group hover:border-[var(--vm-sage)] transition-colors"
                            >
                                <div
                                    className="relative h-40 w-full overflow-hidden"
                                    style={{ background: b.photo?.avg_color || "var(--vm-input)" }}
                                    data-testid={`beneficial-photo-${b.slug}`}
                                >
                                    {b.photo?.thumb_url ? (
                                        <img
                                            src={b.photo.thumb_url}
                                            alt={b.photo.alt || b.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center text-5xl opacity-60">{b.emoji}</div>
                                    )}
                                    <span className={`absolute top-2 right-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${style.bg} ${style.text} ${style.border} backdrop-blur-sm bg-opacity-80`}>
                                        {b.role}
                                    </span>
                                </div>
                                <div className="p-5">
                                    <h3 className="font-serif-display text-2xl text-[var(--vm-text)] mb-1 group-hover:text-[var(--vm-sage)]">{b.name}</h3>
                                    {b.preys_on && b.preys_on.length > 0 ? (
                                        <p className="text-sm text-[var(--vm-text-secondary)]">
                                            Preys on: <span className="text-[var(--vm-text)]">{b.preys_on.join(", ")}</span>
                                        </p>
                                    ) : (
                                        <p className="text-sm text-[var(--vm-text-secondary)]">{b.notes?.slice(0, 100)}</p>
                                    )}
                                    {b.photo?.photographer && (
                                        <div className="mt-3 text-[10px] text-[var(--vm-text-muted)]">
                                            Photo by {b.photo.photographer} on Pexels
                                        </div>
                                    )}
                                    <div className="mt-2 flex items-center gap-1 text-xs text-[var(--vm-sage)] opacity-0 group-hover:opacity-100 transition-opacity">
                                        How to encourage <ArrowRight className="w-3 h-3" />
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </section>
            )}
        </div>
    );
}
